import { FC, useMemo } from "react";
import {
  ICPGeographyWinLossDistribution,
  ICPGeographyDistribution,
} from "../../../../apis/companySearch/icpDistribution";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { hasWinLossData } from "../../utils";
import { GenericICPWinLossChart } from "./GenericICPWinLossChart";

interface ICPGeographyWinLossChartProps {
  /** Win/loss distribution data from API */
  data: ICPGeographyWinLossDistribution[];
  /** Height of the chart container */
  height?: string;
  /** Optional total count to display (overrides calculated total from data) */
  totalCount?: number;
  /** Whether the chart is loading */
  isLoading?: boolean;
  /** Distribution data to determine ordering (optional) */
  distributionData?: ICPGeographyDistribution[];
}

/**
 * Component that displays win/loss geography distribution chart with stacked columns
 * Each column shows win and loss segments that total 100%
 */
export const ICPGeographyWinLossChart: FC<ICPGeographyWinLossChartProps> = ({
  data,
  height = "350px",
  totalCount,
  isLoading = false,
  distributionData,
}) => {
  const { countries } = useTaxonomyContext();

  const countriesMap = useMemo(() => {
    const map = {} as Record<string, string>;
    for (const country of countries || []) {
      if (country.alpha2) {
        map[country.alpha2] = country.name || "";
      }
    }
    return map;
  }, [countries]);

  const getCategoryName = useMemo(
    () => (item: ICPGeographyWinLossDistribution) =>
      countriesMap[item.countryCode] || item.countryCode,
    [countriesMap]
  );

  // Filter and sort to match distribution chart exactly
  // Only show countries that are visible in the distribution chart, in the same order
  const sortData = useMemo(
    () =>
      (
        a: ICPGeographyWinLossDistribution,
        b: ICPGeographyWinLossDistribution
      ) => {
        if (distributionData && distributionData.length > 0) {
          // Get the top 10 countries from distribution data (same logic as ICPGeographyChart)
          const visibleCountries = [...distributionData]
            .sort((x, y) => y.share - x.share)
            .slice(0, 10)
            .map((item, index) => ({
              countryCode: item.countryCode,
              order: index,
            }));

          const orderMap = new Map(
            visibleCountries.map((item) => [item.countryCode, item.order])
          );

          const orderA = orderMap.get(a.countryCode) ?? 999;
          const orderB = orderMap.get(b.countryCode) ?? 999;

          return orderA - orderB; // Sort by distribution order (ascending order index)
        }

        // Fallback to total count descending if no distribution data
        return b.totalCount - a.totalCount;
      },
    [distributionData]
  );

  // Filter function to only show countries that are visible in the distribution chart
  const filterData = useMemo(() => {
    if (distributionData && distributionData.length > 0) {
      // Get the country codes that are visible in the distribution chart (top 10)
      const visibleCountryCodes = new Set(
        [...distributionData]
          .sort((x, y) => y.share - x.share)
          .slice(0, 10)
          .map((item) => item.countryCode)
      );

      return (item: ICPGeographyWinLossDistribution) =>
        item.totalCount > 0 && visibleCountryCodes.has(item.countryCode);
    }

    // Fallback filter if no distribution data
    return (item: ICPGeographyWinLossDistribution) => item.totalCount > 0;
  }, [distributionData]);

  // Show skeleton when loading, or don't render if there's no meaningful win/loss data when not loading
  if (!isLoading && !hasWinLossData(data)) {
    return null;
  }

  return (
    <GenericICPWinLossChart
      data={data}
      getCategoryName={getCategoryName}
      height={height}
      totalCount={totalCount}
      sortData={sortData}
      filterData={filterData}
      segment="Geography"
      isLoading={isLoading}
    />
  );
};
